import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, InputAdornment,
  TablePagination,
  TextField, } from '@mui/material';
import DeleteConfirmationModal from '../../components/shared/modals/DeleteConfirmationModal';
import ViewModal from './components/ViewModal';
import AmendmentModal from './components/AmendmentModal';
import AmendmentTable from './components/AmendmentTable';
import useAmendmentPermissions from './hooks/useAmendmentPermissions';
import * as amendmentApi from '../../api/Amendments/api';
import { CirclePlus, Search } from "lucide-react";
import Toast from '../../components/shared/Toast';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import { extractErrorMessages } from '../../utils/errorHandler';

const Amendments = () => {
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [amendmentModalOpen, setAmendmentModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAmendment, setSelectedAmendment] = useState(null);
  const [viewType, setViewType] = useState("reason");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [modalMode, setModalMode] = useState('add');

  const {
    loading: loadingPermissions,
    canView,
    canAdd,
    canEdit,
    canDelete,
    canApprove,
  } = useAmendmentPermissions();

  const fetchAmendments = async () => {
    try {
      const response = await amendmentApi.getAmendments({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      if (response?.data) {
        const formattedData = response.data.map((amendment) => ({
          id: amendment.id,
          projection: amendment.projection,
          product: amendment.product_name,
          projectedQuantity: amendment.projected_quantity,
          proposedQuantity: amendment.final_quantity,
          reason: amendment.reason || "N/A",
          status: amendment.status,
          reviewerNotes: amendment.reviewer_notes || "N/A",
          proposedBy: amendment.proposed_by,
          reviewedBy: amendment.reviewed_by || "N/A",
          reviewedAt: amendment.reviewed_at,
          createdAt: amendment.created_at,
          updatedAt: amendment.updated_at,
        }));
        setAmendments(formattedData);
        setTotal(response.total);
      } else {
        console.error('Invalid response format:', response);
        showErrorToast('Invalid data format received from server');
        setAmendments([]);
        setTotal(0);
      }
    } catch (error) {
      const messages = extractErrorMessages(error);
      messages.forEach(message => {
        showErrorToast(message);
      });
      setAmendments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAmendments();
  }, [page, rowsPerPage, searchQuery]);

  const handleViewReason = (amendment) => {
    setSelectedAmendment(amendment);
    setViewType("reason");
    setViewModalOpen(true);
  };

  const handleViewNotes = (amendment) => {
    setSelectedAmendment(amendment);
    setViewType("notes");
    setViewModalOpen(true);
  };

  const handleEditAmendment = (amendment) => {
    setSelectedAmendment(amendment);
    setModalMode('edit');
    setAmendmentModalOpen(true);
  };

  // const handleReviewAmendment = (amendment) => {
  //   setSelectedAmendment(amendment);
  //   setModalMode('review');
  //   setAmendmentModalOpen(true);
  // };

  const handleReviewAmendment = (amendment) => {
    setSelectedAmendment({ ...amendment, action: 'review' });
    setModalMode('review');
    setAmendmentModalOpen(true);
  };

  const handleStatusClick = (amendment) => {
    if (!canEdit) {
      showErrorToast('You do not have permission to edit amendments.');
      return;
    }
    setSelectedAmendment(amendment);
    setAmendmentModalOpen(true);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete) {
      showErrorToast('You do not have permission to delete amendments.', 'Permission Denied');
      return;
    }
    try {
      await amendmentApi.deleteAmendment(selectedAmendment.id);
      setDeleteModalOpen(false);
      setSelectedAmendment(null);
      await fetchAmendments();
      showSuccessToast('Amendment deleted successfully');
    } catch (error) {
      const messages = extractErrorMessages(error);
      messages.forEach(message => {
        showErrorToast(message);
      });
    }
  };

  const handleAmendmentSubmit = async (formData, projectionId) => {
    console.log("Form Data: ", formData);
    try {
      if (selectedAmendment) {
        if (selectedAmendment.action === 'review') {
          // Handle review
          await amendmentApi.reviewAmendment(selectedAmendment.id, formData);
          showSuccessToast('Amendment reviewed successfully');
        } else {
          // Handle regular update
          await amendmentApi.updateAmendment(selectedAmendment.id, formData);
          showSuccessToast('Amendment updated successfully');
        }
      } else {
        // Handle new submission (using submit endpoint)
        await amendmentApi.submitAmendment(projectionId, formData);
        showSuccessToast('Amendment submitted successfully');
      }
      
      setAmendmentModalOpen(false);
      setSelectedAmendment(null);
      await fetchAmendments();
    } catch (error) {
      if (formData.status === "PENDING"){
        showErrorToast("Status must be either APPROVED or REJECTED");
      }
      else {
        const messages = extractErrorMessages(error);
        messages.forEach(message => {
          showErrorToast(message);
        });
      }
    }
  };

  const filteredAmendments = amendments.filter((amendment) =>
    Object.values(amendment).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  useEffect(() => {
    if (canView) {
      fetchAmendments();
    }
  }, [canView]);

  if (!canView) {
    showErrorToast('You do not have permission to view amendments.');
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Box
          sx={{
            p: 3,
            backgroundColor: "#ffebee",
            borderRadius: 1,
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography color="textSecondary">
            You don't have permission to view amendments.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Amendments
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />
        {canAdd && (
          <Button
            variant="contained"
            startIcon={<CirclePlus size={18} />}
            onClick={() => {
              setSelectedAmendment(null);
              setAmendmentModalOpen(true);
            }}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": {
                backgroundColor: "#1a614d !important",
              },
            }}
          >
            Add Amendment
          </Button>
        )}
      </Box>

      <AmendmentTable
        amendments={filteredAmendments}
        loading={loading}
        onEdit={(row) => {
          setSelectedAmendment(row);
          setAmendmentModalOpen(true);
        }}
        onDelete={(row) => {
          setSelectedAmendment(row);
          setDeleteModalOpen(true);
        }}
        onViewReason={handleViewReason}
        onViewNotes={handleViewNotes}
        // onStatusClick={handleStatusClick}
        canEdit={canEdit}
        canDelete={canDelete}
        canApprove={canApprove}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onReview={handleReviewAmendment}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          ),
        }}
      />

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Amendment"
        content="Are you sure you want to delete this amendment?"
      />

      <ViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        amendment={selectedAmendment}
        type={viewType}
      />

      <AmendmentModal
        open={amendmentModalOpen}
        onClose={() => setAmendmentModalOpen(false)}
        onSubmit={handleAmendmentSubmit}
        amendment={selectedAmendment}
        // mode={selectedAmendment ? 'edit' : 'add'}
        mode={modalMode}
      />

      <Toast />
    </Box>
  );
};

export default Amendments;
